/**
 * Block 12 PR-D-A Phase D5 Step 10 — local auto-counter для именования КП
 * `{Тип мебели} №{N}` (PRD §10.12.10 «Именование сохранённых КП»).
 *
 * **Locality:** counter живёт в **отдельной IDB DB `quote-counters`** —
 * никакого backend sync (PR-D-A scope). После реализации Backend Workers
 * (PR-D-B либо позже) — этот модуль будет заменён network-call'ом для
 * cross-device consistency. BACKLOG B-114.
 *
 * **Per-furnitureType isolation:** counter для «Кухня» независим от «Шкаф».
 * Key = furnitureType string. Value = последнее использованное число.
 *
 * **Atomic read-modify-write:** `getNextQuoteCounter` использует
 * readwrite-транзакцию — два параллельных save'а одного типа не приведут к
 * race condition (browser IDB сериализует readwrite на одном store).
 *
 * **Graceful degradation:** если IDB unavailable → fallback на in-memory
 * Map (один-tab session counter). Counter всё равно даёт unique numbering
 * в рамках сессии, просто без durability.
 *
 * Reference: TZ Phase D5 Step 10 + ADR-0019 §Q9 + BACKLOG B-114 (backend sync).
 */

import { openDB, type IDBPDatabase, type DBSchema } from 'idb';

export const COUNTERS_DB_NAME = 'quote-counters';
export const COUNTERS_DB_VERSION = 1;
export const COUNTERS_STORE = 'counters';

export interface QuoteCountersDBSchema extends DBSchema {
  counters: {
    key: string; // furnitureType
    value: number; // last used counter for this type
  };
}

let dbPromise: Promise<IDBPDatabase<QuoteCountersDBSchema>> | null = null;
const memoryFallback = new Map<string, number>();

function isIdbAvailable(): boolean {
  return typeof indexedDB !== 'undefined';
}

function logWarn(scope: string, err: unknown): void {
  if (import.meta.env.DEV) {
    console.warn(`[quoteCounter] ${scope} failed`, err);
  }
}

async function getDb(): Promise<IDBPDatabase<QuoteCountersDBSchema> | null> {
  if (!isIdbAvailable()) return null;
  if (!dbPromise) {
    dbPromise = openDB<QuoteCountersDBSchema>(COUNTERS_DB_NAME, COUNTERS_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(COUNTERS_STORE)) {
          db.createObjectStore(COUNTERS_STORE);
        }
      },
      terminated() {
        dbPromise = null;
      },
      blocking() {
        dbPromise?.then((db) => db.close()).catch(() => {});
        dbPromise = null;
      },
    });
  }
  try {
    return await dbPromise;
  } catch (err) {
    logWarn('openDB', err);
    dbPromise = null;
    return null;
  }
}

/**
 * Atomic increment counter для типа мебели. Возвращает следующее
 * available число (1, 2, 3, ...).
 *
 * Fallback на in-memory Map если IDB недоступен — counter всё равно
 * выдаёт unique numbers в рамках текущей сессии.
 */
export async function getNextQuoteCounter(furnitureType: string): Promise<number> {
  const db = await getDb();
  if (!db) {
    // In-memory fallback
    const current = memoryFallback.get(furnitureType) ?? 0;
    const next = current + 1;
    memoryFallback.set(furnitureType, next);
    return next;
  }

  try {
    const tx = db.transaction(COUNTERS_STORE, 'readwrite');
    const current = (await tx.store.get(furnitureType)) ?? 0;
    const next = current + 1;
    await tx.store.put(next, furnitureType);
    await tx.done;
    return next;
  } catch (err) {
    logWarn('getNextQuoteCounter', err);
    // Fallback to memory если IDB транзакция взорвалась mid-flight
    const current = memoryFallback.get(furnitureType) ?? 0;
    const next = current + 1;
    memoryFallback.set(furnitureType, next);
    return next;
  }
}

/** Peek current value без increment'а (для preview UI / отладки). */
export async function peekQuoteCounter(furnitureType: string): Promise<number> {
  const db = await getDb();
  if (!db) return memoryFallback.get(furnitureType) ?? 0;
  try {
    return (await db.get(COUNTERS_STORE, furnitureType)) ?? 0;
  } catch (err) {
    logWarn('peekQuoteCounter', err);
    return memoryFallback.get(furnitureType) ?? 0;
  }
}

/** Clears counter for type (e.g. testing utility либо future admin tool). */
export async function clearQuoteCounter(furnitureType: string): Promise<void> {
  memoryFallback.delete(furnitureType);
  const db = await getDb();
  if (!db) return;
  try {
    await db.delete(COUNTERS_STORE, furnitureType);
  } catch (err) {
    logWarn('clearQuoteCounter', err);
  }
}

/** Очищает все counters (logout-style). */
export async function clearAllQuoteCounters(): Promise<void> {
  memoryFallback.clear();
  const db = await getDb();
  if (!db) return;
  try {
    await db.clear(COUNTERS_STORE);
  } catch (err) {
    logWarn('clearAllQuoteCounters', err);
  }
}

/** Test cleanup utility. */
export function _resetQuoteCounterCacheForTests(): void {
  dbPromise = null;
  memoryFallback.clear();
}
